// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: Settings > Notifications > Pop-ups inside Konvo (design/assistant-everywhere t5). The desktop
// section: which messages pop up, Konvo's draft, holding them while you type and quiet in calls,
// with a sample card on the right. The phone page draws the same choices as its own card
// (PhoneNotificationsTab) from the pieces exported here.

import {SettingsSection} from '@app/features/app/components/dialogs/shared/SettingsSection';
import styles from '@app/features/konvo_popups/components/InAppPopupSettings.module.css';
import InAppPopupSettings, {
	IN_APP_POPUP_MODES,
	type InAppPopupMode,
} from '@app/features/konvo_popups/state/InAppPopupSettings';
import type {PopupEntry} from '@app/features/konvo_popups/utils/InAppPopupModel';
import {Switch} from '@app/features/ui/components/form/FormSwitch';
import {Segmented} from '@app/features/ui/konvo_controls/KonvoControls';
import type {MessageDescriptor} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import {lazy, Suspense} from 'react';

export const POPUPS_SECTION_DESCRIPTOR = msg({
	message: 'Pop-ups inside Konvo',
	comment: 'Settings > Notifications: section heading for the message pop-ups shown inside the app window.',
});
export const POPUPS_SECTION_HINT_DESCRIPTOR = msg({
	message: 'While Konvo is in front, new messages pop up here instead of as a notification.',
	comment: 'Settings > Notifications > Pop-ups inside Konvo: what the section is about.',
});
export const SHOW_POPUPS_FOR_DESCRIPTOR = msg({
	message: 'Show pop-ups for',
	comment: 'Settings > Notifications > Pop-ups inside Konvo: label of the Off / DMs and mentions / Everything choice.',
});
export const SHOW_POPUPS_FOR_HINT_DESCRIPTOR = msg({
	message: 'Never for the chat you have open',
	comment: 'Settings > Notifications > Pop-ups inside Konvo: hint under "Show pop-ups for".',
});
export const POPUP_MODE_DESCRIPTORS: Record<InAppPopupMode, MessageDescriptor> = {
	off: msg({
		message: 'Off',
		comment: 'Settings > Notifications > Pop-ups inside Konvo: no pop-ups, notifications as before. Keep it short.',
	}),
	direct: msg({
		message: 'DMs and mentions',
		comment:
			'Settings > Notifications > Pop-ups inside Konvo: pop-ups for direct messages and mentions only. Keep it short.',
	}),
	all: msg({
		message: 'Everything',
		comment:
			'Settings > Notifications > Pop-ups inside Konvo: pop-ups for every message that would notify. Keep it short.',
	}),
};
export const SHOW_DRAFT_DESCRIPTOR = msg({
	message: "Show Konvo's draft",
	comment:
		'Settings > Notifications > Pop-ups inside Konvo: switch for the "Draft ready" chip and Konvo\'s drafted reply.',
});
export const SHOW_DRAFT_HINT_DESCRIPTOR = msg({
	message: '"Draft ready" on the pop-up and the draft in the reply box',
	comment: 'Settings > Notifications > Pop-ups inside Konvo: hint under "Show Konvo\'s draft". Keep the quotes.',
});
export const WAIT_TYPING_DESCRIPTOR = msg({
	message: "Wait while I'm typing",
	comment: 'Settings > Notifications > Pop-ups inside Konvo: switch that holds pop-ups while the user types.',
});
export const WAIT_TYPING_HINT_DESCRIPTOR = msg({
	message: "Hold pop-ups until you've stopped typing for 2 s",
	comment:
		'Settings > Notifications > Pop-ups inside Konvo: hint under "Wait while I\'m typing". "2 s" is two seconds.',
});
export const QUIET_CALLS_DESCRIPTOR = msg({
	message: 'Quiet in calls',
	comment: 'Settings > Notifications > Pop-ups inside Konvo: switch that stops pop-ups during calls and voice mode.',
});
export const QUIET_CALLS_HINT_DESCRIPTOR = msg({
	message: 'No pop-ups in a call or voice mode; notifications work as before',
	comment: 'Settings > Notifications > Pop-ups inside Konvo: hint under "Quiet in calls".',
});
const PREVIEW_DESCRIPTOR = msg({
	message: 'Preview',
	comment: 'Settings > Notifications > Pop-ups inside Konvo: heading above a sample pop-up.',
});
const PREVIEW_TEXT_DESCRIPTOR = msg({
	message: 'Are we still on for 3? I can bring the scoring sheet.',
	comment: 'Settings > Notifications > Pop-ups inside Konvo: the text of the sample pop-up (a WhatsApp message).',
});

/* The sample card is drawn by the pop-ups themselves; load them with the section. */
const PreviewCard = lazy(() =>
	import('@app/features/konvo_popups/components/InAppPopups').then((module) => ({
		default: module.InAppPopupPreviewCard,
	})),
);

export const PopupModeChoice = observer(({compact = false, grow = false}: {compact?: boolean; grow?: boolean}) => {
	const {i18n} = useLingui();
	return (
		<Segmented<InAppPopupMode>
			value={InAppPopupSettings.mode}
			options={IN_APP_POPUP_MODES.map((value) => ({
				value,
				children: i18n._(POPUP_MODE_DESCRIPTORS[value]),
				'data-flx': `konvo-popups.settings.mode.${value}`,
			}))}
			onChange={(mode) => InAppPopupSettings.set({mode})}
			compact={compact}
			grow={grow}
			aria-label={i18n._(SHOW_POPUPS_FOR_DESCRIPTOR)}
			data-flx="konvo-popups.settings.mode"
		/>
	);
});

/** A sample pop-up (a WhatsApp message) as the settings show it. */
export const InAppPopupPreview = observer(() => {
	const {i18n} = useLingui();
	const sample: PopupEntry = {
		key: 'sample',
		messageId: 'sample',
		source: 'whatsapp',
		target: {kind: 'whatsapp', jid: 'sample'},
		sender: 'Alex Morgan',
		senderKey: 'sample',
		groupName: null,
		groupChat: false,
		avatar: {kind: 'initials', name: 'Alex Morgan'},
		reason: 'direct',
		preview: i18n._(PREVIEW_TEXT_DESCRIPTOR),
		sticky: false,
		draftable: true,
		at: Date.now(),
		count: 1,
		mixed: false,
		deadline: null,
		firstAt: Date.now(),
		draftSignature: null,
	};
	return (
		<Suspense fallback={null}>
			<PreviewCard entry={sample} draftReady={InAppPopupSettings.showDraft} />
		</Suspense>
	);
});

export const InAppPopupSettingsSection = observer(() => {
	const {i18n} = useLingui();
	const off = InAppPopupSettings.mode === 'off';
	return (
		<SettingsSection
			id="in-app-popups"
			title={i18n._(POPUPS_SECTION_DESCRIPTOR)}
			description={i18n._(POPUPS_SECTION_HINT_DESCRIPTOR)}
			data-flx="konvo-popups.settings"
		>
			<div className={styles.layout}>
				<div className={styles.rows}>
					<div className={styles.modeRow}>
						<span className={styles.modeText}>
							<span className={styles.modeLabel}>{i18n._(SHOW_POPUPS_FOR_DESCRIPTOR)}</span>
							<span className={styles.modeHint}>{i18n._(SHOW_POPUPS_FOR_HINT_DESCRIPTOR)}</span>
						</span>
						<PopupModeChoice compact={true} />
					</div>
					<Switch
						label={i18n._(SHOW_DRAFT_DESCRIPTOR)}
						description={i18n._(SHOW_DRAFT_HINT_DESCRIPTOR)}
						value={InAppPopupSettings.showDraft}
						disabled={off}
						onChange={(showDraft) => InAppPopupSettings.set({showDraft})}
						data-flx="konvo-popups.settings.show-draft"
					/>
					<Switch
						label={i18n._(WAIT_TYPING_DESCRIPTOR)}
						description={i18n._(WAIT_TYPING_HINT_DESCRIPTOR)}
						value={InAppPopupSettings.waitWhileTyping}
						disabled={off}
						onChange={(waitWhileTyping) => InAppPopupSettings.set({waitWhileTyping})}
						data-flx="konvo-popups.settings.wait-typing"
					/>
					<Switch
						label={i18n._(QUIET_CALLS_DESCRIPTOR)}
						description={i18n._(QUIET_CALLS_HINT_DESCRIPTOR)}
						value={InAppPopupSettings.quietInCalls}
						disabled={off}
						onChange={(quietInCalls) => InAppPopupSettings.set({quietInCalls})}
						data-flx="konvo-popups.settings.quiet-calls"
					/>
				</div>
				<div className={styles.preview} aria-hidden="true">
					<span className={styles.previewLabel}>{i18n._(PREVIEW_DESCRIPTOR)}</span>
					<div className={styles.previewStage}>
						<InAppPopupPreview />
					</div>
				</div>
			</div>
		</SettingsSection>
	);
});
